<template>
    <div class="card card-with-shadow border-0 mb-4">
        <div class="card-header bg-transparent d-flex flex-wrap justify-content-between align-items-center px-4 py-3">
            <h4 class="m-0 p-0">{{ $t('Orders') }}</h4>
            <div class="d-flex align-items-center" style="gap:8px;">
                <label class="form-label mb-0">{{ $t('Practitioner') }}</label>
                <select class="form-control form-control-sm" style="min-width:180px;" v-model="selectedPractitioner">
                    <option value="all">{{ $t('All') }}</option>
                    <option v-for="p in practitioners" :key="p.id" :value="p.id">{{ p.name }}</option>
                </select>
            </div>
        </div>

        <div class="card-body">
            <app-overlay-loader v-if="countsLoading" />
            <div v-else class="row">
                <div v-for="card in cards" :key="card.type" class="col-6 col-md-4 col-lg-3 mb-3">
                    <div class="order-stat-card" :class="{ active: activeType === card.type }"
                        :style="{ borderLeftColor: colorHex(card.color) }" @click="selectCard(card)">
                        <div class="count" :style="{ color: colorHex(card.color) }">
                            {{ counts[card.type] != null ? counts[card.type] : '–' }}
                        </div>
                        <div class="title">{{ $t(card.title) }}</div>
                        <div v-if="card.type === 'awaiting' && counts.awaiting_red > 0" class="sub text-danger">
                            {{ counts.awaiting_red }} {{ $t('over 30 days') }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- expandable order list for the active card -->
            <div v-if="activeType" class="mt-2 border-top pt-3">
                <h6 class="mb-2">{{ $t(activeCardTitle) }}</h6>
                <app-overlay-loader v-if="listLoading" />
                <div v-else class="table-responsive">
                    <table class="table table-hover table-sm mb-0">
                        <thead>
                            <tr>
                                <th>{{ $t('Order ID') }}</th>
                                <th>{{ $t('Order Date') }}</th>
                                <th>{{ $t('Patient Name') }}</th>
                                <th>{{ $t('Branch') }}</th>
                                <th>{{ $t('Order Status') }}</th>
                                <th>{{ $t('Payment Status') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="!listRows.length"><td colspan="6" class="text-center text-muted">{{ $t('No orders.') }}</td></tr>
                            <tr v-for="row in listRows" :key="row.id"
                                :style="row.tier ? { color: row.tier === 'red' ? '#dc3545' : '#fd7e14' } : {}">
                                <td>
                                    <a :href="orderHref(row.id)" target="_blank" rel="noopener" class="text-primary">
                                        {{ row.order_ref }}
                                    </a>
                                </td>
                                <td>{{ row.order_date }}</td>
                                <td>{{ row.patient }}</td>
                                <td>{{ row.branch }}</td>
                                <td><span v-if="row.order_status.name" :class="`badge badge-${row.order_status.class} badge-pill`">{{ $t(row.order_status.name) }}</span></td>
                                <td><span v-if="row.payment_status.name" :class="`badge badge-${row.payment_status.class} badge-pill`">{{ $t(row.payment_status.name) }}</span></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import { axiosGet, urlGenerator } from "../../../../common/Helper/AxiosHelper";
import { ORDER_DASHBOARD_COUNTS, ORDER_DASHBOARD_LIST, ORDER_DASHBOARD_PRACTITIONERS } from "../../../Config/ApiUrl-CP";

export default {
    name: "OrdersDashboardSection",
    data() {
        return {
            countsLoading: false,
            listLoading: false,
            counts: {},
            practitioners: [],
            selectedPractitioner: 'all',
            activeType: null,
            listRows: [],
            cards: [
                { type: 'open', title: 'Total Open Orders', color: '' },
                { type: 'delayed', title: 'Delayed Orders', color: 'red' },
                { type: 'extremely_delayed', title: 'Extremely Delayed Orders', color: 'maroon' },
                { type: 'approaching', title: 'Orders Approaching Target', color: 'orange' },
                { type: 'for_trial', title: 'Orders For Trial', color: '' },
                { type: 'for_dispatch', title: 'Orders for Dispatch', color: '' },
                { type: 'for_collection', title: 'Orders for Collection', color: '' },
                { type: 'awaiting', title: 'Awaiting Response', color: 'orange' },
                { type: 'dispatched_not_delivered', title: 'Items Dispatched (not Delivered)', color: '' },
            ],
        };
    },
    computed: {
        ...mapGetters(['getBranchOrWarehouseId']),
        activeCardTitle() {
            const c = this.cards.find(c => c.type === this.activeType);
            return c ? c.title : '';
        },
    },
    methods: {
        colorHex(color) {
            return { red: '#dc3545', maroon: '#800000', orange: '#fd7e14' }[color] || '#3874ff';
        },
        queryParams() {
            const params = [];
            if (this.getBranchOrWarehouseId) params.push(`branch_or_warehouse_id=${this.getBranchOrWarehouseId}`);
            params.push(`practitioner_id=${this.selectedPractitioner || 'all'}`);
            return params.join('&');
        },
        fetchCounts() {
            this.countsLoading = true;
            axiosGet(`${ORDER_DASHBOARD_COUNTS}?${this.queryParams()}`).then(res => {
                this.counts = res.data || {};
            }).finally(() => { this.countsLoading = false; });
        },
        fetchPractitioners() {
            const branch = this.getBranchOrWarehouseId ? `branch_or_warehouse_id=${this.getBranchOrWarehouseId}` : '';
            axiosGet(`${ORDER_DASHBOARD_PRACTITIONERS}?${branch}`).then(res => {
                this.practitioners = res.data || [];
            }).catch(() => { this.practitioners = []; });
        },
        selectCard(card) {
            if (this.activeType === card.type) {
                this.activeType = null;
                this.listRows = [];
                return;
            }
            this.activeType = card.type;
            this.fetchList(card.type);
        },
        fetchList(type) {
            this.listLoading = true;
            this.listRows = [];
            axiosGet(`${ORDER_DASHBOARD_LIST}?type=${type}&${this.queryParams()}`).then(res => {
                this.listRows = (res.data && res.data.rows) ? res.data.rows : [];
            }).finally(() => { this.listLoading = false; });
        },
        orderHref(id) {
            return urlGenerator(`order/details/${id}`);
        },
    },
    mounted() {
        this.fetchPractitioners();
        this.fetchCounts();
    },
    watch: {
        getBranchOrWarehouseId() {
            this.selectedPractitioner = 'all';
            this.activeType = null;
            this.listRows = [];
            this.fetchPractitioners();
            this.fetchCounts();
        },
        selectedPractitioner() {
            this.fetchCounts();
            if (this.activeType) this.fetchList(this.activeType);
        },
    },
};
</script>

<style scoped>
.order-stat-card {
    border: 1px solid #eef0f3;
    border-left: 4px solid #3874ff;
    border-radius: 6px;
    padding: 14px 16px;
    height: 100%;
    cursor: pointer;
    transition: box-shadow .15s, transform .05s;
}
.order-stat-card:hover { box-shadow: 0 2px 10px rgba(0,0,0,.08); }
.order-stat-card.active { box-shadow: 0 0 0 2px rgba(56,116,255,.35); }
.order-stat-card .count { font-size: 26px; font-weight: 700; line-height: 1.1; }
.order-stat-card .title { font-size: 13px; color: #5b6b79; margin-top: 2px; }
.order-stat-card .sub { font-size: 11px; margin-top: 2px; }
</style>
